import { useState, useEffect } from "react";
import { puzzleImages } from "./GameData";

const ILLUSTRATED_PUZZLES = [
  { id: "unicorn_castle", name_it: "Unicorno e Castello", name_en: "Unicorn & Castle", url: "https://static.prod-images.emergentagent.com/jobs/4eb1603e-8e1c-4d34-b9d8-53ec8bb0c9c4/images/a13caafc3d40d672a1f3a2536c4a81f9c9aa1384ef6901972d94b3079d27d6e5.jpeg" },
  { id: "space_rocket", name_it: "Razzo nello Spazio", name_en: "Space Rocket", url: "https://static.prod-images.emergentagent.com/jobs/4eb1603e-8e1c-4d34-b9d8-53ec8bb0c9c4/images/c46572825d7fa835bb76cbcdeb0ba9c688775890218a9e8a000b2ae558f429f1.jpeg" },
  { id: "jungle", name_it: "Giungla", name_en: "Jungle", url: "https://static.prod-images.emergentagent.com/jobs/4eb1603e-8e1c-4d34-b9d8-53ec8bb0c9c4/images/d2c93ecb2e74c463bca2f300ef84e9e9821f3fe7f24972155683349902b93b18.jpeg" },
  { id: "underwater", name_it: "Mondo Sottomarino", name_en: "Underwater World", url: "https://static.prod-images.emergentagent.com/jobs/4eb1603e-8e1c-4d34-b9d8-53ec8bb0c9c4/images/e3caddbe4f512d424770768d77f02a8c68f2d174b7f570b14a9f467071450dcb.jpeg" },
];

export function PuzzleGame({ t, lang, ageGroup, onBack, onComplete }) {
  const [selectedPuzzle, setSelectedPuzzle] = useState(null);
  const [pieces, setPieces] = useState([]);
  const [moves, setMoves] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);

  // Grid size: default by age, user-selectable
  const [gridSize, setGridSize] = useState(ageGroup === "6-7" ? 3 : 4);
  const totalPieces = gridSize * gridSize;

  useEffect(() => {
    let timer;
    if (startTime && !completed) {
      timer = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [startTime, completed]);

  const [selectedIndex, setSelectedIndex] = useState(null);

  const initializePuzzle = (puzzle) => {
    setSelectedPuzzle(puzzle);
    setMoves(0);
    setCompleted(false);
    setStartTime(Date.now());
    setElapsedTime(0);
    setSelectedIndex(null);
    // Swap puzzle: every tile filled, shuffled
    const initial = Array.from({ length: gridSize * gridSize }, (_, i) => i);
    let shuffled = [...initial];
    do {
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
    } while (shuffled.every((v, i) => v === i));
    setPieces(shuffled);
  };

  const handleTileClick = (index) => {
    if (completed) return;
    if (selectedIndex === null) {
      setSelectedIndex(index);
      return;
    }
    if (selectedIndex === index) {
      setSelectedIndex(null);
      return;
    }
    const newPieces = [...pieces];
    [newPieces[selectedIndex], newPieces[index]] = [newPieces[index], newPieces[selectedIndex]];
    setPieces(newPieces);
    setSelectedIndex(null);
    const newMoves = moves + 1;
    setMoves(newMoves);
    if (newPieces.every((v, i) => v === i)) {
      setCompleted(true);
      if (onComplete) {
        onComplete({ moves: newMoves, time: elapsedTime });
      }
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Puzzle selection screen
  if (!selectedPuzzle) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-500 via-blue-500 to-purple-500 p-4">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={onBack}
            className="bg-white/20 text-white px-4 py-2 rounded-full font-bold hover:bg-white/30 mb-4"
            data-testid="puzzle-back-btn"
          >
            ← {t.backHome}
          </button>
          
          <h2 className="text-3xl font-bold text-white text-center mb-1" data-testid="puzzle-title">🧩 {t.puzzleTitle}</h2>
          <p className="text-center text-white/90 mb-4 text-sm">{lang === "it" ? "Tocca due tessere per scambiarle e ricomponi l'immagine!" : "Tap two tiles to swap them and rebuild the picture!"}</p>
          
          {/* Difficulty selector */}
          <div className="flex justify-center gap-2 mb-6" data-testid="puzzle-difficulty">
            {[3, 4, 5].map((size) => (
              <button
                key={size}
                onClick={() => setGridSize(size)}
                className={`px-5 py-2 rounded-full font-bold transition-all ${gridSize === size ? "bg-white text-blue-600" : "bg-white/30 text-white hover:bg-white/50"}`}
                data-testid={`puzzle-grid-${size}`}
              >
                {size}x{size} ({size * size} {t.pieces})
              </button>
            ))}
          </div>
          
          {/* Illustrated puzzles */}
          <h3 className="text-white font-bold mb-3">🖼️ {t.illustratedLabel}</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {ILLUSTRATED_PUZZLES.map((img) => (
              <button
                key={img.id}
                onClick={() => initializePuzzle(img)}
                className="bg-white rounded-2xl p-2 shadow-lg hover:scale-105 transition-transform"
                data-testid={`puzzle-photo-${img.id}`}
              >
                <img src={img.url} alt={lang === "it" ? img.name_it : img.name_en} className="w-full aspect-square object-cover rounded-xl mb-2" />
                <div className="font-bold text-gray-700 text-sm pb-1">{lang === "it" ? img.name_it : img.name_en}</div>
              </button>
            ))}
          </div>
          
          {/* Emoji puzzles */}
          <h3 className="text-white font-bold mb-3">😀 {t.emojiLabel}</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {puzzleImages.map((img) => (
              <button
                key={img.id}
                onClick={() => initializePuzzle(img)}
                className="bg-white rounded-2xl p-6 shadow-lg hover:scale-105 transition-transform"
                data-testid={`puzzle-image-${img.id}`}
              >
                <div className="text-6xl mb-2">{img.emoji}</div>
                <div className="font-bold text-gray-700">{lang === "it" ? img.name_it : img.name_en}</div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Puzzle game screen
  const emojiSvg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><rect width='100' height='100' fill='#ffffff'/><text x='50' y='54' font-size='102' text-anchor='middle' dominant-baseline='central'>${selectedPuzzle.emoji || ""}</text></svg>`;
  const emojiBg = selectedPuzzle.url
    ? `url("${selectedPuzzle.url}")`
    : `url("data:image/svg+xml,${encodeURIComponent(emojiSvg)}")`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-500 via-blue-500 to-purple-500 p-4">
      <div className="max-w-lg mx-auto">
        <button
          onClick={() => setSelectedPuzzle(null)}
          className="bg-white/20 text-white px-4 py-2 rounded-full font-bold hover:bg-white/30 mb-4"
          data-testid="puzzle-back-btn"
        >
          ← {t.backHome}
        </button>
        
        {/* Stats */}
        <div className="flex justify-center gap-6 mb-4">
          <div className="bg-white/20 px-4 py-2 rounded-full text-white font-bold" data-testid="puzzle-moves">
            {t.moves}: {moves}
          </div>
          <div className="bg-white/20 px-4 py-2 rounded-full text-white font-bold" data-testid="puzzle-time">
            {t.time}: {formatTime(elapsedTime)}
          </div>
        </div>
        
        {/* Completed message */}
        {completed && (
          <div className="bg-green-100 text-green-700 p-4 rounded-2xl text-center mb-4 font-bold text-xl" data-testid="puzzle-completed">
            🎉 {t.completed}
          </div>
        )}
        
        {/* Puzzle Grid */}
        <div 
          className="bg-white rounded-2xl p-2 shadow-xl mx-auto"
          style={{ 
            display: "grid",
            gridTemplateColumns: `repeat(${gridSize}, 1fr)`,
            gap: "4px",
            maxWidth: "400px"
          }}
        >
          {pieces.map((piece, index) => {
            const inPlace = piece === index;
            return (
            <div
              key={index}
              onClick={() => handleTileClick(index)}
              data-testid={`puzzle-piece-${index}`}
              className={`aspect-square relative overflow-hidden rounded-lg cursor-pointer transition-transform ${
                selectedIndex === index
                  ? "ring-4 ring-pink-500 scale-95 z-10"
                  : inPlace && !completed
                    ? "ring-2 ring-green-400"
                    : "hover:scale-105"
              }`}
              style={{
                backgroundImage: emojiBg,
                backgroundSize: `${gridSize * 100}% ${gridSize * 100}%`,
                backgroundPosition: `${(piece % gridSize) * (100 / (gridSize - 1))}% ${Math.floor(piece / gridSize) * (100 / (gridSize - 1))}%`
              }}
            >
            </div>
            );
          })}
        </div>
        
        {/* Reference image */}
        <div className="mt-4 text-center">
          <div className="text-white mb-2">{lang === "it" ? selectedPuzzle.name_it : selectedPuzzle.name_en}</div>
          {selectedPuzzle.url ? (
            <img src={selectedPuzzle.url} alt="reference" className="w-24 h-24 object-cover rounded-xl mx-auto shadow-lg" data-testid="puzzle-reference-img" />
          ) : (
            <div className="text-8xl">{selectedPuzzle.emoji}</div>
          )}
        </div>
        
        {/* Shuffle button */}
        <button
          onClick={() => initializePuzzle(selectedPuzzle)}
          className="w-full mt-4 py-3 rounded-xl bg-white text-purple-600 font-bold hover:bg-gray-100"
          data-testid="puzzle-shuffle-btn"
        >
          🔀 {t.shuffle}
        </button>
      </div>
    </div>
  );
}

export default PuzzleGame;
